import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import ImageUploadField from './ImageUploadField';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface EquipoMember {
  id: string;
  nombre: string;
  rol: string;
  especialidad: string;
  experiencia: string;
  imagen: string;
}

const EquipoAdminTab = () => {
  const [equipo, setEquipo] = useState<EquipoMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'equipo')
        .maybeSingle();
      if (data?.value) {
        setEquipo(((data.value as EquipoMember[]) ?? []).map((m, i) => ({
          id: m.id ?? `e-${i}`,
          nombre: m.nombre ?? '',
          rol: m.rol ?? '',
          especialidad: m.especialidad ?? '',
          experiencia: m.experiencia ?? '',
          imagen: m.imagen ?? '',
        })));
      }
      setLoading(false);
    };
    load();
  }, []);

  const addMember = () => {
    setEquipo((p) => [...p, { id: `e-${Date.now()}`, nombre: '', rol: '', especialidad: '', experiencia: '', imagen: '' }]);
  };

  const removeMember = (id: string) => setEquipo((p) => p.filter((m) => m.id !== id));

  const updateMember = (id: string, key: keyof EquipoMember, val: string) =>
    setEquipo((p) => p.map((m) => m.id === id ? { ...m, [key]: val } : m));

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${SUPABASE_URL}/functions/v1/bali-save-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ updates: [{ key: 'equipo', value: equipo.map(({ id: _id, ...rest }) => ({ ...rest, id: _id })) }] }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Equipo del taller</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{equipo.length} miembro(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addMember}
            className="flex items-center gap-1.5 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-add-line" /> Añadir miembro
          </button>
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 ${
              saved ? 'bg-green-500 text-white' : 'bg-yellow-400 hover:bg-yellow-300 text-black'
            }`}>
            <i className={saving ? 'ri-loader-4-line animate-spin' : saved ? 'ri-check-line' : 'ri-save-line'} />
            {saving ? 'Guardando…' : saved ? '¡Guardado!' : 'Guardar'}
          </button>
        </div>
      </div>

      {equipo.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-800 flex flex-col items-center justify-center py-20 text-zinc-600">
          <i className="ri-team-line text-4xl mb-3" />
          <p className="text-sm">Sin miembros añadidos. Pulsa &quot;Añadir miembro&quot; para empezar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {equipo.map((m) => (
            <div key={m.id} className="bg-zinc-900 border border-zinc-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-zinc-800 text-zinc-400">
                    <i className="ri-user-line text-sm" />
                  </div>
                  <span className="text-zinc-300 text-xs font-bold uppercase tracking-wider">
                    {m.nombre || 'Nuevo miembro'}
                  </span>
                </div>
                <button onClick={() => removeMember(m.id)}
                  className="w-7 h-7 flex items-center justify-center text-zinc-600 hover:text-red-400 cursor-pointer transition-colors">
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {([
                  ['nombre', 'Nombre completo'],
                  ['rol', 'Rol / Cargo'],
                  ['especialidad', 'Especialidad'],
                  ['experiencia', 'Experiencia'],
                ] as const).map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
                    <input
                      value={m[key] ?? ''}
                      onChange={(e) => updateMember(m.id, key, e.target.value)}
                      placeholder={key === 'experiencia' ? 'Ej: 10 años de experiencia' : ''}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors"
                    />
                  </div>
                ))}
              </div>

              <ImageUploadField
                label="Foto del miembro"
                value={m.imagen}
                onChange={(val) => updateMember(m.id, 'imagen', val)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipoAdminTab;
